import axios from 'axios';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { API_URL } from '../config/api';
import ConfirmModal from './common/ConfirmModal';
import './WorkshopManager.css';

const WorkshopManager = () => {
  const [workshops, setWorkshops] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingWorkshop, setEditingWorkshop] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    image: '',
    date: '',
    time: '',
    price: '',
    currency: 'INR',
    isLive: true,
    isRecorded: false,
    contentType: 'live',
    videoUrl: '',
    meetingLink: '',
    duration: '',
    instructor: 'Admin',
    maxParticipants: '',
    tags: [],
    isPublished: false,
  });
  const [uploading, setUploading] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [confirmModal, setConfirmModal] = useState({
    show: false,
    title: '',
    message: '',
    onConfirm: null,
    type: 'danger'
  });

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchWorkshops();
  }, []);

  const fetchWorkshops = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/workshops`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setWorkshops(response.data.data);
    } catch (error) {
      toast.error('Failed to fetch workshops');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const uploadFormData = new FormData();
    uploadFormData.append('image', file);
    uploadFormData.append('uploadType', 'workshop');

    try {
      const response = await axios.post(`${API_URL}/api/upload`, uploadFormData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
      });

      const baseImageUrl = response.data.data.baseS3Url || response.data.data.url;
      setFormData({ ...formData, image: baseImageUrl });

      try {
        const previewResponse = await axios.post(
          `${API_URL}/api/upload/presigned-url`,
          { url: baseImageUrl, expiresIn: 86400 },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setPreviewImageUrl(previewResponse.data?.data?.url || baseImageUrl);
      } catch (previewError) {
        console.warn('Failed to fetch pre-signed preview URL, falling back to base URL preview:', previewError);
        setPreviewImageUrl(baseImageUrl);
      }

      toast.success('Image uploaded successfully!');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Image upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleContentTypeChange = (e) => {
    const contentType = e.target.value;
    setFormData({
      ...formData,
      contentType,
      isLive: contentType === 'live' || contentType === 'both',
      isRecorded: contentType === 'recorded' || contentType === 'both',
    });
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData({
        ...formData,
        tags: [...formData.tags, tagInput.trim()],
      });
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag) => {
    setFormData({
      ...formData,
      tags: formData.tags.filter((t) => t !== tag),
    });
  };

  const handleSubmit = async (e) => {
    // Prevent any default form behavior and event bubbling
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required. Please login again.');
        return false;
      }
      
      const submitData = {
        ...formData,
        price: parseFloat(formData.price),
        maxParticipants: formData.maxParticipants ? parseInt(formData.maxParticipants) : null,
        date: new Date(formData.date),
      };

      if (editingWorkshop) {
        await axios.put(
          `${API_URL}/api/workshops/${editingWorkshop._id}`,
          submitData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Workshop updated successfully!');
      } else {
        await axios.post(`${API_URL}/api/workshops`, submitData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Workshop created successfully!');
      }
      fetchWorkshops();
      resetForm();
    } catch (error) {
      console.error('Workshop submit error:', error);
      // Handle authentication errors specifically
      if (error.response?.status === 401) {
        toast.error('Authentication failed. Please login again.');
      } else {
        toast.error(error.response?.data?.message || error.response?.data?.error || 'Operation failed');
      }
      // Don't reset form on error - keep form data so user can fix and retry
      return false;
    }
  };

  const handleEdit = (workshop) => {
    setEditingWorkshop(workshop);
    const dateStr = workshop.date ? new Date(workshop.date).toISOString().split('T')[0] : '';
    const baseImageUrl = (workshop.image || '').split('?')[0].split('&')[0].split('#')[0];
    setFormData({
      title: workshop.title || '',
      description: workshop.description || '',
      image: baseImageUrl,
      date: dateStr,
      time: workshop.time || '',
      price: workshop.price || '',
      currency: workshop.currency || 'INR',
      isLive: workshop.isLive !== undefined ? workshop.isLive : true,
      isRecorded: workshop.isRecorded || false,
      contentType: workshop.contentType || 'live',
      videoUrl: workshop.videoUrl || '',
      meetingLink: workshop.meetingLink || '',
      duration: workshop.duration || '',
      instructor: workshop.instructor || 'Admin',
      maxParticipants: workshop.maxParticipants || '',
      tags: workshop.tags || [],
      isPublished: workshop.isPublished || false,
    });
    setPreviewImageUrl(workshop.image || baseImageUrl || '');
    setShowForm(true);
  };

  const handleTogglePublish = async (workshop) => {
    try {
      const newPublishedStatus = !workshop.isPublished;
      const updateData = { 
        isPublished: newPublishedStatus,
        // Ensure isActive is true when publishing
        ...(newPublishedStatus && { isActive: true })
      };
      await axios.put(
        `${API_URL}/api/workshops/${workshop._id}`,
        updateData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Workshop ${newPublishedStatus ? 'published' : 'unpublished'} successfully!`);
      fetchWorkshops();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update workshop status');
    }
  };

  const handleDelete = (id) => {
    setConfirmModal({
      show: true,
      title: 'Delete Workshop',
      message: 'Are you sure you want to delete this workshop? This action cannot be undone.',
      type: 'danger',
      onConfirm: async () => {
        try {
          await axios.delete(`${API_URL}/api/workshops/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          toast.success('Workshop deleted successfully!');
          setConfirmModal({ show: false, title: '', message: '', onConfirm: null, type: 'danger' });
          fetchWorkshops();
        } catch (error) {
          toast.error(error.response?.data?.message || 'Failed to delete workshop');
          setConfirmModal({ show: false, title: '', message: '', onConfirm: null, type: 'danger' });
        }
      }
    });
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      image: '',
      date: '',
      time: '',
      price: '',
      currency: 'INR',
      isLive: true,
      isRecorded: false,
      contentType: 'live',
      videoUrl: '',
      meetingLink: '',
      duration: '',
      instructor: 'Admin',
      maxParticipants: '',
      tags: [],
      isPublished: false,
    });
    setTagInput('');
    setPreviewImageUrl('');
    setEditingWorkshop(null);
    setShowForm(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    return timeString;
  };

  return (
    <div className="workshop-manager">
      <div className="header">
        <h2>Workshop Manager</h2>
        <button onClick={() => setShowForm(!showForm)} className="btn-primary">
          {showForm ? 'Cancel' : '+ Add Workshop'}
        </button>
      </div>

      {showForm && (
        <div className="form-container">
          <h3>{editingWorkshop ? 'Edit Workshop' : 'Add New Workshop'}</h3>
          <form 
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleSubmit(e);
            }} 
            noValidate
          >
            <div className="form-group">
              <label>Title *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label>Description *</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows="6"
                required
              />
            </div>

            <div className="form-group">
              <label>Image *</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                disabled={uploading}
              />
              {uploading && <p>Uploading...</p>}
              {(previewImageUrl || formData.image) && (
                <div className="image-preview">
                  <img src={previewImageUrl || formData.image} alt="Preview" />
                </div>
              )}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Date *</label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Time *</label>
                <input
                  type="time"
                  value={formData.time}
                  onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Price *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Currency *</label>
                <select
                  value={formData.currency}
                  onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                  required
                >
                  <option value="INR">INR</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>Content Type *</label>
              <select
                value={formData.contentType}
                onChange={handleContentTypeChange}
                required
              >
                <option value="live">Live</option>
                <option value="recorded">Recorded</option>
                <option value="both">Both (Live + Recorded)</option>
              </select>
            </div>

            {formData.contentType === 'live' || formData.contentType === 'both' ? (
              <div className="form-group">
                <label>Meeting Link</label>
                <input
                  type="url"
                  value={formData.meetingLink}
                  onChange={(e) => setFormData({ ...formData, meetingLink: e.target.value })}
                  placeholder="https://meet.google.com/..."
                />
              </div>
            ) : null}

            {formData.contentType === 'recorded' || formData.contentType === 'both' ? (
              <div className="form-group">
                <label>Video URL</label>
                <input
                  type="url"
                  value={formData.videoUrl}
                  onChange={(e) => setFormData({ ...formData, videoUrl: e.target.value })}
                  placeholder="https://youtube.com/..."
                />
              </div>
            ) : null}

            <div className="form-row">
              <div className="form-group">
                <label>Duration</label>
                <input
                  type="text"
                  value={formData.duration}
                  onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                  placeholder="e.g., 2 hours"
                />
              </div>

              <div className="form-group">
                <label>Instructor</label>
                <input
                  type="text"
                  value={formData.instructor}
                  onChange={(e) => setFormData({ ...formData, instructor: e.target.value })}
                />
              </div>
            </div>

            <div className="form-group">
              <label>Max Participants</label>
              <input
                type="number"
                min="1"
                value={formData.maxParticipants}
                onChange={(e) => setFormData({ ...formData, maxParticipants: e.target.value })}
                placeholder="Leave empty for unlimited"
              />
            </div>

            <div className="form-group">
              <label>Tags</label>
              <div className="tag-input-group">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddTag();
                    }
                  }}
                  placeholder="Add tag and press Enter"
                />
                <button type="button" onClick={handleAddTag} className="btn-add-tag">
                  Add
                </button>
              </div>
              {formData.tags.length > 0 && (
                <div className="tags-list">
                  {formData.tags.map((tag, idx) => (
                    <span key={idx} className="tag">
                      {tag}
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(tag)}
                        className="tag-remove"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.isPublished}
                  onChange={(e) => setFormData({ ...formData, isPublished: e.target.checked })}
                />
                Publish (make visible to public)
              </label>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-primary">
                {editingWorkshop ? 'Update' : 'Create'} Workshop
              </button>
              <button type="button" onClick={resetForm} className="btn-secondary">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <p>Loading workshops...</p>
      ) : (
        <div className="workshops-grid">
          {workshops.map((workshop) => (
            <div key={workshop._id} className="workshop-card">
              {workshop.image && <img src={workshop.image} alt={workshop.title} />}
              <div className="workshop-info">
                <h4>{workshop.title}</h4>
                <p className="workshop-date">
                  📅 {formatDate(workshop.date)} at {formatTime(workshop.time)}
                </p>
                <p className="workshop-price">
                  💰 {workshop.currency} {workshop.price}
                </p>
                <p className="workshop-content-type">
                  {workshop.contentType === 'live' && '🔴 Live'}
                  {workshop.contentType === 'recorded' && '📹 Recorded'}
                  {workshop.contentType === 'both' && '🔴📹 Live + Recorded'}
                </p>
                <p className="workshop-status">
                  Status: {workshop.isPublished ? (
                    <span style={{ color: workshop.isActive ? '#28a745' : '#ffc107' }}>
                      {workshop.isActive ? '✅ Published & Visible' : '⚠️ Published but Inactive'}
                    </span>
                  ) : (
                    <span style={{ color: '#666' }}>📝 Draft (Not visible to users)</span>
                  )}
                </p>
                {workshop.description && (
                  <p className="workshop-description">{workshop.description.substring(0, 100)}...</p>
                )}
                <div className="workshop-actions">
                  <button 
                    onClick={() => handleTogglePublish(workshop)} 
                    className={workshop.isPublished ? "btn-unpublish" : "btn-publish"}
                    title={workshop.isPublished ? "Click to unpublish" : "Click to publish"}
                  >
                    {workshop.isPublished ? 'Unpublish' : 'Publish'}
                  </button>
                  <button onClick={() => handleEdit(workshop)} className="btn-edit">
                    Edit
                  </button>
                  <button onClick={() => handleDelete(workshop._id)} className="btn-delete">
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmModal
        show={confirmModal.show}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ show: false, title: '', message: '', onConfirm: null, type: 'danger' })}
        confirmText="Delete"
        cancelText="Cancel"
        type={confirmModal.type}
      />
    </div>
  );
};

export default WorkshopManager;

